import React, {Suspense} from 'react';

import './App.css';
import LoginPage from '../features/login/LoginPage';
import Notifications from '../features/notifications/Notifications';
import {ToastProvider} from 'react-toast-notifications';
import ErrorBoundary from './ErrorBoundary';
import ThemeProvider from 'providers/ThemeProvider';

import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';

import {DialogTypes} from 'common/names';

import {DndProvider} from 'react-dnd';
import {HTML5Backend} from 'react-dnd-html5-backend';
import {useHistoryChanger} from 'common/hooks/useHistoryChanger';
import {CustomToast, CustomToastContainer} from 'features/notifications/NotificationsList/Toast';
import {useTitle} from 'common/hooks/useTitle';
import Loader from 'components/Loader';

import {store} from 'redux/configStore';
import {updateFullStatesSaga, updateStatesSaga} from 'features/layout/redux/sagas/states';
import {Saga} from 'redux-saga';
import {useCheckLicenseExpiration} from 'common/hooks/useCheckLicenseExpiration';
import {SankeyPage} from 'features/hipaa/SankeyPage';
import {SamlCallback} from 'features/login/SamlCallback';
import {
   Route,
   RouterProvider,
   createBrowserRouter,
   createRoutesFromElements,
} from 'react-router-dom';
import {RootLayout} from './RootLayout';
// import {DashboardPage} from 'features/layout/CommonPage/Dashboard';

const updateStatesSagaInjector = (page: any) => {
   if (process.env.REACT_APP_DISABLE_STATES_UPDATE !== 'true') {
      window.useSinceForStates && store.injectSaga('updateStates', updateStatesSaga as Saga);
      store.injectSaga('updateFullStates', updateFullStatesSaga as Saga);
   }
   return page;
};

const CommonPage = React.lazy(() =>
   import('../features/layout/CommonPage').then(updateStatesSagaInjector)
);
const CommonRoutes = React.lazy(() =>
   import('../features/layout/CommonPage/CommonPage').then(updateStatesSagaInjector)
);

const SFTPFilesystem = React.lazy(() =>
   import('features/layout/CommonPage/panels/SFTP/SFTPFilesystem').then(updateStatesSagaInjector)
);
const SearchPartnerForm = React.lazy(
   () => import('features/layout/DialogRoutes/dialogs/partners/SearchPartnerForm')
);
const SearchTransactionForm = React.lazy(() =>
   import('features/layout/DialogRoutes/dialogs/transactions/SearchTransactionForm').then(
      updateStatesSagaInjector
   )
);
const SearchOpenPGPKeysForm = React.lazy(
   () => import('features/layout/DialogRoutes/dialogs/keys/SearchOpenPGPKeysForm')
);
const SearchSSHClientKeysForm = React.lazy(
   () => import('features/layout/DialogRoutes/dialogs/keys/SearchSSHClientKeysForm')
);
const SearchSSLKeysForm = React.lazy(
   () => import('features/layout/DialogRoutes/dialogs/keys/SearchSSLKeysForm')
);
const JobMonitorPage = React.lazy(() => import('features/job-monitor'));

const JobHistoryViewerPage = React.lazy(() => import('features/job-monitor/JobHistoryViewer'));
const JobFilesViewerPage = React.lazy(() => import('features/job-monitor/JobFilesViewer'));

const RunNowPanelOutsideWrapper = React.lazy(
   () =>
      import(
         'features/layout/CommonPage/panels/TransactionPanel/SchedulePanel/RunNow/RunNowPanelOutiseWrapper'
      )
);

const router = createBrowserRouter(
   createRoutesFromElements(
      <Route element={<RootLayout />}>
         <Route path="/login" element={<LoginPage />} />
         <Route path="/login/saml-complete" element={<SamlCallback />} />

         <Route
            path={`/dialog/${DialogTypes.SearchPartnerDialog.id}`}
            element={<SearchPartnerForm />}
         />

         <Route
            path={`/dialog/${DialogTypes.SearchTransactionsDialog.id}`}
            element={<SearchTransactionForm />}
         />

         <Route
            path={`/dialog/${DialogTypes.SearchOpenPGPKeysDialog.id}`}
            element={<SearchOpenPGPKeysForm />}
         />

         <Route
            path={`/dialog/${DialogTypes.SearchSSHClientKeysDialog.id}`}
            element={<SearchSSHClientKeysForm />}
         />

         <Route
            path={`/dialog/${DialogTypes.SearchSSLKeysDialog.id}`}
            element={<SearchSSLKeysForm />}
         />

         <Route
            path={`/dialog/${DialogTypes.RunNowTransactionDialog.id}`}
            element={<RunNowPanelOutsideWrapper />}
         />

         <Route path="/jobmonitor" element={<JobMonitorPage />} />
         <Route path="/jobhistories" element={<JobHistoryViewerPage />} />
         <Route path="/filehistories" element={<JobFilesViewerPage />} />
         <Route path="/connectionsmap" element={<SankeyPage />} />

         <Route path="/" element={<CommonPage />}>
            {/* <Route path={VFS_PATH + '/*'} element={<SFTPFilesystem />} /> */}
            <Route path="vfs/*" element={<SFTPFilesystem />} />
            <Route path=":objectType/:id" element={<CommonRoutes />} />
            <Route index element={<CommonRoutes />} />
         </Route>
      </Route>
   )
);

const App = () => {
   useHistoryChanger();
   useTitle();
   useCheckLicenseExpiration();

   return (
      <ThemeProvider>
         <LocalizationProvider dateAdapter={AdapterDayjs}>
            <ErrorBoundary>
               <Suspense fallback={<Loader />}>
                  <ToastProvider
                     autoDismissTimeout={10000}
                     components={{
                        ToastContainer: CustomToastContainer,
                        Toast: CustomToast,
                     }}
                  >
                     <DndProvider backend={HTML5Backend}>
                        <RouterProvider router={router} />
                     </DndProvider>

                     <Notifications />
                  </ToastProvider>
               </Suspense>
            </ErrorBoundary>
         </LocalizationProvider>
      </ThemeProvider>
   );
};

export default App;
